import { z } from 'zod'

// Issue types for business flag reports
export const IssueTypeEnum = z.enum([
	'broken_link',
	'wrong_category',
	'wrong_business',
	'closed',
	'other',
])

// Feedback variant types
export const FeedbackVariantEnum = z.enum(['flag', 'feedback', 'contact'])

// Base schema shared by all variants
const BaseFeedbackSchema = z.object({
	variant: FeedbackVariantEnum,
	email: z.email({ message: 'Please enter a valid email address' }),
	message: z
		.string()
		.max(2000, { message: 'Message must be less than 2000 characters' })
		.optional()
		.or(z.literal('')),
	// Context fields (optional)
	businessId: z.string().optional().or(z.literal('')),
	businessName: z.string().optional().or(z.literal('')),
	pageUrl: z.string().optional().or(z.literal('')),
})

// Flag variant - requires issue type
const FlagSchema = BaseFeedbackSchema.extend({
	variant: z.literal('flag'),
	issue: z.enum(
		['broken_link', 'wrong_category', 'wrong_business', 'closed', 'other'],
		{
			message: 'Please select an issue type',
		},
	),
})

// Feedback variant - general feedback
const FeedbackSchema = BaseFeedbackSchema.extend({
	variant: z.literal('feedback'),
})

// Contact variant - general contact
const ContactSchema = BaseFeedbackSchema.extend({
	variant: z.literal('contact'),
	message: z
		.string({ message: 'Message is required' })
		.min(1, { message: 'Message is required' })
		.max(2000, { message: 'Message must be less than 2000 characters' }),
})

// Union schema for all variants
export const SubmitFeedbackSchema = z.discriminatedUnion('variant', [
	FlagSchema,
	FeedbackSchema,
	ContactSchema,
])

export type SubmitFeedback = z.infer<typeof SubmitFeedbackSchema>
export type FeedbackVariant = z.infer<typeof FeedbackVariantEnum>
export type IssueType = z.infer<typeof IssueTypeEnum>

// Helper to get issue label
export function getIssueLabel(issue: IssueType): string {
	const labels: Record<IssueType, string> = {
		broken_link: 'Broken Link',
		wrong_category: 'Wrong Category',
		wrong_business: 'Wrong Business Information',
		closed: 'Business Closed',
		other: 'Other Issue',
	}
	return labels[issue]
}

// Helper to get variant label
export function getVariantLabel(variant: FeedbackVariant): string {
	const labels: Record<FeedbackVariant, string> = {
		flag: 'Business Report',
		feedback: 'Site Feedback',
		contact: 'Contact',
	}
	return labels[variant]
}
